import api from './api';
import { PaginationParams, ApiResponse, GatewaySMS, GatewaySMSFormData, GatewaySMSResponse, GatewaySMSConnectionTest, GatewaySMSStats } from '../types';

export interface CreateGatewaySMSRequest {
  nome: string;
  tipo: 'twilio' | 'zenvia' | 'torpedussms' | 'infobip';
  ativo?: boolean;
  is_default?: boolean;
  
  // Campos para Twilio
  account_sid?: string;
  auth_token?: string;
  phone_number?: string;
  
  // Campos para Zenvia
  api_token_zenvia?: string;
  from_zenvia?: string;
  
  // Campos para TorpedusSMS
  usuario_torpedussms?: string;
  senha_torpedussms?: string;
  
  // Campos para Infobip
  api_key_infobip?: string;
  base_url_infobip?: string;
  sender_name_infobip?: string;
  titulo?: string;
  
  // Webhook (opcional para todos)
  webhook_url?: string;
  
  // Observações
  observacoes?: string;
}

export interface UpdateGatewaySMSRequest {
  nome?: string;
  tipo?: 'twilio' | 'zenvia' | 'torpedussms' | 'infobip';
  ativo?: boolean;
  is_default?: boolean;
  
  // Campos para Twilio
  account_sid?: string;
  auth_token?: string;
  phone_number?: string;
  
  // Campos para Zenvia
  api_token_zenvia?: string;
  from_zenvia?: string;
  
  // Campos para TorpedusSMS
  usuario_torpedussms?: string;
  senha_torpedussms?: string;
  
  // Campos para Infobip
  api_key_infobip?: string;
  base_url_infobip?: string;
  sender_name_infobip?: string;
  titulo?: string;
  
  // Webhook (opcional para todos)
  webhook_url?: string;
  
  // Observações
  observacoes?: string;
}

class GatewaySMSService {
  /**
   * Lista todos os gateways SMS com paginação
   */
  async getAllGatewaysSMS(params?: PaginationParams & { 
    tipo?: string; 
    ativo?: string; 
  }): Promise<GatewaySMSResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.tipo) queryParams.append('tipo', params.tipo);
      if (params?.ativo) queryParams.append('ativo', params.ativo);

      const url = `/gateways-sms?${queryParams.toString()}`;
      console.log('🔗 Fazendo requisição para:', url);

      const response = await api.get<ApiResponse<GatewaySMS[]> | GatewaySMS[]>(url);
      
      console.log('📡 Resposta bruta da API:', response.data);
      
      // Verificar se a resposta é um array direto ou objeto estruturado
      if (Array.isArray(response.data)) {
        const result = {
          gateways: response.data,
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 10,
            total: response.data.length,
            pages: 1,
          },
        };
        
        console.log('✅ Resultado processado (array):', result);
        return result;
      }
      
      // Resposta é objeto estruturado
      const apiResponse = response.data as ApiResponse<GatewaySMS[]>;
      if (apiResponse.success && apiResponse.data) {
        const result = {
          gateways: apiResponse.data,
          pagination: apiResponse.pagination || {
            page: 1,
            limit: 10,
            total: apiResponse.data.length,
            pages: 1,
          },
        };
        
        console.log('✅ Resultado processado (objeto):', result);
        return result;
      }
      
      throw new Error(apiResponse.message || 'Erro ao buscar gateways SMS');
    } catch (error: any) {
      console.error('❌ Erro no gatewaySmsService.getAllGatewaysSMS:', error);
      
      if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
        throw new Error('Erro de conexão com o servidor');
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca gateways SMS ativos para seleção
   */
  async getActiveGatewaysSMS(): Promise<GatewaySMS[]> {
    try {
      const response = await api.get<GatewaySMS[]>('/gateways-sms/active');
      
      // Acessar response.data que contém os dados reais
      if (response.data && typeof response.data === 'object') {
        // Se tem success e data, significa que é uma resposta estruturada da API
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }
        
        // Se é um array, é a resposta direta
        if (Array.isArray(response.data)) {
          return response.data;
        }
      }
      
      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateways SMS ativos:', error);
      throw error;
    }
  }

  /**
   * Busca um gateway SMS específico por ID
   */
  async getGatewaySMSById(id: number): Promise<GatewaySMS> {
    try {
      const response = await api.get<GatewaySMS>(`/gateways-sms/${id}`);
      
      // Acessar response.data que é o que realmente contém os dados
      if (response.data && typeof response.data === 'object') {
        // Se tem success e data, significa que é uma resposta estruturada da API
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }
        
        // É a resposta direta
        return response.data;
      }
      
      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateway SMS por ID:', error);
      throw error;
    }
  }

  /**
   * Busca gateways SMS por tipo
   */
  async getGatewaysSMSByType(tipo: 'twilio' | 'zenvia' | 'torpedussms' | 'infobip'): Promise<GatewaySMS[]> {
    try {
      const response = await api.get<GatewaySMS[]>(`/gateways-sms/tipo/${tipo}`);
      
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }
        
        if (Array.isArray(response.data)) {
          return response.data;
        }
      }
      
      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateways SMS por tipo:', error);
      throw error;
    }
  }

  /**
   * Cria um novo gateway SMS
   */
  async createGatewaySMS(gatewayData: CreateGatewaySMSRequest): Promise<GatewaySMS> {
    try {
      console.log('📤 Criando gateway SMS:', gatewayData);
      
      const response = await api.post<GatewaySMS>('/gateways-sms', gatewayData);
      
      console.log('✅ Gateway SMS criado:', response.data);
      
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }
        
        return response.data;
      }
      
      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('❌ Erro ao criar gateway SMS:', error);
      throw error;
    }
  }

  /**
   * Atualiza um gateway SMS
   */
  async updateGatewaySMS(id: number, gatewayData: UpdateGatewaySMSRequest): Promise<GatewaySMS> {
    try {
      console.log('📤 Atualizando gateway SMS:', { id, data: gatewayData });
      
      const response = await api.put<GatewaySMS>(`/gateways-sms/${id}`, gatewayData);
      
      console.log('✅ Gateway SMS atualizado:', response.data);
      
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }
        
        return response.data;
      }
      
      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('❌ Erro ao atualizar gateway SMS:', error);
      throw error;
    }
  }

  /**
   * Exclui um gateway SMS
   */
  async deleteGatewaySMS(id: number): Promise<void> {
    try {
      console.log('🗑️ Excluindo gateway SMS:', id);
      
      await api.delete(`/gateways-sms/${id}`);
      
      console.log('✅ Gateway SMS excluído');
    } catch (error: any) {
      console.error('❌ Erro ao excluir gateway SMS:', error);
      throw error;
    }
  }

  /**
   * Alterna o status de um gateway SMS
   */
  async toggleGatewaySMSStatus(id: number): Promise<GatewaySMS> {
    try {
      console.log('🔄 Alternando status do gateway SMS:', id);
      
      const response = await api.patch<GatewaySMS>(`/gateways-sms/${id}/toggle-status`);
      
      console.log('✅ Status do gateway SMS alternado:', response.data);
      
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }
        
        return response.data;
      }
      
      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('❌ Erro ao alternar status do gateway SMS:', error);
      throw error;
    }
  }

  /**
   * Define um gateway SMS como padrão
   */
  async setGatewaySMSAsDefault(id: number): Promise<GatewaySMS> {
    try {
      console.log('⭐ Definindo gateway SMS como padrão:', id);
      
      const response = await api.patch<GatewaySMS>(`/gateways-sms/${id}/set-default`);
      
      console.log('✅ Gateway SMS definido como padrão:', response.data);
      
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }
        
        return response.data;
      }
      
      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('❌ Erro ao definir gateway SMS como padrão:', error);
      throw error;
    }
  }

  /**
   * Testa a conectividade de um gateway SMS
   */
  async testGatewaySMSConnection(id: number, phoneNumber: string): Promise<{
    success: boolean;
    message: string;
    data?: any;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const response = await api.post<any>(`/gateways-sms/${id}/test-connection`, { phoneNumber });
      
      // O interceptor axios retorna response.data do backend
      // O backend envia: { success: true, message: "...", data: {...} }
      const responseData = response as any;
      
      return {
        success: responseData?.success || false,
        message: responseData?.message || 'Teste concluído',
        data: responseData?.data || responseData,
        error: responseData?.error || null,
        statusCode: responseData?.success ? 200 : 500
      };
      
    } catch (error: any) {
      console.error('❌ Erro ao testar conectividade do gateway SMS:', error);
      
      // Extrair informações do erro da API
      const errorResponse = error.response?.data;
      const statusCode = error.response?.status || 500;
      
      let message = 'Erro ao testar conectividade';
      let errorDetail = null;
      
      if (errorResponse) {
        message = errorResponse.message || message;
        errorDetail = errorResponse.error || errorResponse.data?.error || null;
      } else if (error.message) {
        message = error.message;
        errorDetail = error.message;
      }
      
      return {
        success: false,
        message,
        data: null,
        error: errorDetail,
        statusCode
      };
    }
  }

  /**
   * Busca estatísticas dos gateways SMS
   */
  async getGatewaySMSStats(): Promise<GatewaySMSStats> {
    try {
      const response = await api.get<GatewaySMSStats>('/gateways-sms/stats');
      
      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }
        
        return response.data;
      }
      
      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar estatísticas dos gateways SMS:', error);
      throw error;
    }
  }

  /**
   * Enviar SMS através de um gateway
   */
  async sendSMS(data: { 
    to: string; 
    message: string; 
    gateway_id?: number; 
  }): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> {
    try {
      const response = await api.post('/gateways-sms/send', data);
      
      if (response.data && typeof response.data === 'object') {
        // Se tem success, é uma resposta estruturada da API
        if ('success' in response.data) {
          return response.data as {
            success: boolean;
            message: string;
            data?: any;
          };
        }
        
        // Resposta direta de sucesso
        return {
          success: true,
          message: 'SMS enviado com sucesso',
          data: response.data
        };
      }
      
      return {
        success: true,
        message: 'SMS enviado com sucesso'
      };
    } catch (error: any) {
      console.error('Erro ao enviar SMS:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Erro ao enviar SMS';
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  // Métodos utilitários para interface
  getGatewaySMSDisplayName(tipo: 'twilio' | 'zenvia' | 'torpedussms' | 'infobip'): string {
    const names = {
      'twilio': 'Twilio',
      'zenvia': 'Zenvia',
      'torpedussms': 'TorpedusSMS',
      'infobip': 'Infobip',
    };
    return names[tipo];
  }

  getGatewaySMSColor(tipo: 'twilio' | 'zenvia' | 'torpedussms' | 'infobip'): string {
    const colors = {
      'twilio': '#F22F46',
      'zenvia': '#0066CC',
      'torpedussms': '#FF6B35',
      'infobip': '#00A651',
    };
    return colors[tipo];
  }

  getGatewaySMSIcon(tipo: 'twilio' | 'zenvia' | 'torpedussms' | 'infobip'): string {
    const icons = {
      'twilio': '📱',
      'zenvia': '📨',
      'torpedussms': '🚀',
      'infobip': '🌐',
    };
    return icons[tipo];
  }

  getStatusConnectivityColor(status: 'conectado' | 'erro' | 'nao_testado'): string {
    const colors = {
      'conectado': '#4CAF50',
      'erro': '#F44336',
      'nao_testado': '#FF9800',
    };
    return colors[status];
  }

  getStatusConnectivityLabel(status: 'conectado' | 'erro' | 'nao_testado'): string {
    const labels = {
      'conectado': 'Conectado',
      'erro': 'Erro',
      'nao_testado': 'Não Testado',
    };
    return labels[status];
  }
}

export const gatewaySmsService = new GatewaySMSService();
export default gatewaySmsService; 